import React, { useEffect, useState } from 'react';
import { X, UserCheck, Search, Check, Clock, AlertTriangle } from 'lucide-react';
import { HitApi } from '../../Api/ApiHit';
import { searchStaffGeneral, updateAppBooking, searchAppBooking } from '../../constant/Constant';
import { getSelectedUnit } from '../../storage/Storage';
import toast from 'react-hot-toast';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const TIME_SLOTS = [
  { start: '08:00', end: '10:00', label: '8 AM - 10 AM' },
  { start: '10:00', end: '12:00', label: '10 AM - 12 PM' },
  { start: '12:00', end: '14:00', label: '12 PM - 2 PM' },
  { start: '14:00', end: '16:00', label: '2 PM - 4 PM' },
  { start: '16:00', end: '18:00', label: '4 PM - 6 PM' },
  { start: '18:00', end: '20:00', label: '6 PM - 8 PM' },
];

const getBookingDay = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return isNaN(date) ? null : DAYS[date.getDay()];
};

const timeToMinutes = (timeStr) => {
  if (!timeStr) return null;
  const clean = timeStr.replace(/\s+/g, '').toUpperCase();
  const match = clean.match(/^(\d{1,2}):?(\d{2})?\s*(AM|PM)?$/);
  if (!match) return null;
  let hours = parseInt(match[1]);
  const mins = parseInt(match[2] || '0');
  const period = match[3];
  if (period === 'PM' && hours < 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  return hours * 60 + mins;
};

const getStaffWindowsForDay = (staff, bookingDate) => {
  if (!staff.workingWindows || staff.workingWindows.length === 0) return [];
  const day = getBookingDay(bookingDate);
  if (!day) return [];
  const dayWindow = staff.workingWindows.find((w) => w.day === day);
  if (!dayWindow || !dayWindow.slots) return [];
  return dayWindow.slots;
};

const isStaffAvailableByWindow = (staff, bookingDate, bookingTime) => {
  if (!staff.workingWindows || staff.workingWindows.length === 0) {
    return { available: true, reason: 'No schedule set' };
  }
  const day = getBookingDay(bookingDate);
  if (!day) return { available: true, reason: '' };
  const dayWindow = staff.workingWindows.find((w) => w.day === day);
  if (!dayWindow || !dayWindow.slots || dayWindow.slots.length === 0) {
    return { available: false, reason: `Not working on ${day}` };
  }
  const bookingMins = timeToMinutes(bookingTime);
  if (bookingMins === null) return { available: true, reason: '' };
  const inSlot = dayWindow.slots.some((slot) => {
    const start = timeToMinutes(slot.start);
    const end = timeToMinutes(slot.end);
    return start !== null && end !== null && bookingMins >= start && bookingMins < end;
  });
  if (!inSlot) {
    return { available: false, reason: `Not available at ${bookingTime}` };
  }
  return { available: true, reason: '' };
};

const AssignStaffModal = ({ booking, onClose, onSaved }) => {
  const [staffList, setStaffList] = useState([]);
  const [staffSearch, setStaffSearch] = useState('');
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [saving, setSaving] = useState(false);
  // assignments: { serviceIndex: { staffId, windows: [{ start, end }] } }
  const [assignments, setAssignments] = useState({});
  const [activeServiceIndex, setActiveServiceIndex] = useState(null);
  const [busyStaffIds, setBusyStaffIds] = useState({});

  useEffect(() => {
    fetchStaff();
    fetchBusyStaff();
    const initial = {};
    booking.services?.forEach((service, index) => {
      if (service.staff) {
        const staffId = typeof service.staff === 'object' ? service.staff._id : service.staff;
        initial[index] = {
          staffId,
          windows: service.assignedWindows || [],
        };
      }
    });
    setAssignments(initial);
  }, []);

  const fetchStaff = (search = '') => {
    setLoadingStaff(true);
    const unit = getSelectedUnit();
    const unitId = unit?._id || unit?.unitIds || null;
    const searchQuery = { isActive: true };
    if (unitId) {
      searchQuery.$or = [
        { unitIds: unitId },
        { unitIds: { $size: 0 } },
        { unitIds: { $exists: false } },
      ];
    }
    if (search) {
      searchQuery.$and = [
        { $or: [
          { name: { $regex: search, $options: 'i' } },
          { phoneNumber: { $regex: search, $options: 'i' } },
        ]}
      ];
    }
    const json = {
      page: 1,
      limit: 100,
      search: searchQuery,
      populate: [
        { path: 'roleId', select: 'name' },
        { path: 'unitIds', select: 'unitName unitCode' }
      ]
    };
    HitApi(json, searchStaffGeneral).then((res) => {
      if (res?.data?.docs) {
        setStaffList(res.data.docs);
      }
    }).catch((err) => {
      console.error('Error fetching staff:', err);
    }).finally(() => {
      setLoadingStaff(false);
    });
  };

  const fetchBusyStaff = () => {
    if (!booking.bookingDate) return;
    const searchQuery = {
      bookingDate: booking.bookingDate,
      bookingStatus: { $in: ['CONFIRMED', 'PENDING'] },
      _id: { $ne: booking._id },
      'services.staff': { $ne: null },
    };
    HitApi({ page: 1, limit: 200, search: searchQuery, sort: { createdAt: -1 } }, searchAppBooking)
      .then((res) => {
        if (res?.statusCode === 200) {
          const docs = res.data?.docs || [];
          const busy = {};
          docs.forEach((b) => {
            (b.services || []).forEach((s) => {
              if (s.staff) {
                const staffId = typeof s.staff === 'object' ? s.staff._id : s.staff;
                if (!busy[staffId]) busy[staffId] = [];
                busy[staffId].push({
                  bookingNumber: b.bookingNumber,
                  time: b.bookingTime,
                  service: s.name,
                  windows: s.assignedWindows || [],
                });
              }
            });
          });
          setBusyStaffIds(busy);
        }
      })
      .catch(() => {});
  };

  const handleStaffSearch = (value) => {
    setStaffSearch(value);
    fetchStaff(value);
  };

  const handleAssign = (serviceIndex, staffId) => {
    setAssignments((prev) => ({
      ...prev,
      [serviceIndex]: { staffId, windows: prev[serviceIndex]?.windows || [] },
    }));
    setActiveServiceIndex(null);
  };

  const handleRemoveAssignment = (serviceIndex) => {
    setAssignments((prev) => {
      const updated = { ...prev };
      delete updated[serviceIndex];
      return updated;
    });
  };

  const toggleWindow = (serviceIndex, slot) => {
    setAssignments((prev) => {
      const current = prev[serviceIndex];
      if (!current) return prev;
      const slotKey = `${slot.start}-${slot.end}`;
      const exists = current.windows.some((w) => `${w.start}-${w.end}` === slotKey);
      const updatedWindows = exists
        ? current.windows.filter((w) => `${w.start}-${w.end}` !== slotKey)
        : [...current.windows, { start: slot.start, end: slot.end }];
      return { ...prev, [serviceIndex]: { ...current, windows: updatedWindows } };
    });
  };

  const isWindowSelected = (serviceIndex, slot) => {
    const current = assignments[serviceIndex];
    if (!current) return false;
    return current.windows.some((w) => w.start === slot.start && w.end === slot.end);
  };

  const isWindowBusy = (staffId, slot) => {
    const busyEntries = busyStaffIds[staffId];
    if (!busyEntries) return null;
    const slotStart = timeToMinutes(slot.start);
    const slotEnd = timeToMinutes(slot.end);
    for (const entry of busyEntries) {
      for (const w of entry.windows || []) {
        const wStart = timeToMinutes(w.start);
        const wEnd = timeToMinutes(w.end);
        if (wStart !== null && wEnd !== null && slotStart < wEnd && slotEnd > wStart) {
          return entry;
        }
      }
      // Also check by booking time if no windows
      if (!entry.windows || entry.windows.length === 0) {
        const entryMins = timeToMinutes(entry.time);
        if (entryMins !== null && entryMins >= slotStart && entryMins < slotEnd) {
          return entry;
        }
      }
    }
    return null;
  };

  const getStaffById = (staffId) => {
    return staffList.find((s) => s._id === staffId);
  };

  const getStaffAvailability = (staff) => {
    const windowCheck = isStaffAvailableByWindow(staff, booking.bookingDate, booking.bookingTime);
    if (!windowCheck.available) {
      return { available: false, reason: windowCheck.reason, type: 'window' };
    }
    const busyEntries = busyStaffIds[staff._id];
    if (busyEntries && busyEntries.length > 0) {
      const bookingMins = timeToMinutes(booking.bookingTime);
      const conflict = busyEntries.find((entry) => {
        const entryMins = timeToMinutes(entry.time);
        if (bookingMins === null || entryMins === null) return false;
        return Math.abs(bookingMins - entryMins) < 60;
      });
      if (conflict) {
        return {
          available: false,
          reason: `Busy: ${conflict.service} at ${conflict.time} (${conflict.bookingNumber})`,
          type: 'busy',
        };
      }
    }
    return { available: true, reason: '', type: 'ok' };
  };

  const getAvailableWindows = (staffId) => {
    const staff = getStaffById(staffId);
    if (!staff) return TIME_SLOTS;
    const staffWindows = getStaffWindowsForDay(staff, booking.bookingDate);
    if (staffWindows.length === 0) return TIME_SLOTS; // No schedule = all available
    return TIME_SLOTS.filter((slot) =>
      staffWindows.some((sw) => sw.start === slot.start && sw.end === slot.end)
    );
  };

  const handleSave = () => {
    setSaving(true);
    const updatedServices = booking.services.map((service, index) => {
      const assignment = assignments[index];
      return {
        ...service,
        staff: assignment?.staffId || null,
        assignedWindows: assignment?.windows || [],
      };
    });

    HitApi({
      _id: booking._id,
      services: updatedServices,
    }, updateAppBooking).then((res) => {
      if (res?.statusCode === 200) {
        toast.success('Staff assigned successfully');
        onSaved();
      } else {
        toast.error(res?.error || 'Failed to assign staff');
      }
    }).catch(() => {
      toast.error('Failed to assign staff');
    }).finally(() => {
      setSaving(false);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Assign Staff</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {booking.bookingNumber} • {booking.bookingDate || 'No date'} {booking.bookingTime ? `• ${booking.bookingTime}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Services List */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {booking.services?.map((service, index) => {
            const assignment = assignments[index];
            const assignedStaffId = assignment?.staffId;
            const assignedStaff = assignedStaffId ? getStaffById(assignedStaffId) : null;
            const isDropdownOpen = activeServiceIndex === index;
            const selectedWindows = assignment?.windows || [];

            return (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                {/* Service Info */}
                <div className="flex items-center gap-3 mb-3">
                  {service.img ? (
                    <img src={service.img} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                      <span className="text-xs font-medium text-gray-500">{index + 1}</span>
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{service.name}</p>
                    <p className="text-xs text-gray-500">
                      ₹{service.pricing?.finalPrice || service.pricing?.basePrice || 0}
                      {service.quantity > 1 && ` × ${service.quantity}`}
                    </p>
                  </div>
                </div>

                {/* Assigned Staff */}
                {assignedStaff ? (
                  <>
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-green-200 flex items-center justify-center">
                          <span className="text-xs font-semibold text-green-800">
                            {assignedStaff.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-green-900">{assignedStaff.name}</p>
                          <p className="text-xs text-green-600">{assignedStaff.phoneNumber}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveAssignment(index)}
                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                      >
                        Remove
                      </button>
                    </div>

                    {/* Time Window Selector */}
                    <div className="mt-2">
                      <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Select time windows for this service
                      </p>
                      <div className="grid grid-cols-3 gap-1.5">
                        {getAvailableWindows(assignedStaffId).map((slot) => {
                          const selected = isWindowSelected(index, slot);
                          const busyConflict = isWindowBusy(assignedStaffId, slot);
                          return (
                            <button
                              key={`${slot.start}-${slot.end}`}
                              onClick={() => !busyConflict && toggleWindow(index, slot)}
                              disabled={!!busyConflict}
                              className={`px-2 py-1.5 text-[11px] font-medium rounded-md transition-colors ${
                                busyConflict
                                  ? 'bg-red-50 text-red-300 cursor-not-allowed border border-red-100'
                                  : selected
                                    ? 'bg-gray-900 text-white'
                                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                              }`}
                              title={busyConflict ? `Busy: ${busyConflict.service} (${busyConflict.bookingNumber})` : ''}
                            >
                              {slot.label}
                              {busyConflict && <span className="block text-[9px] text-red-400">Busy</span>}
                            </button>
                          );
                        })}
                      </div>
                      {selectedWindows.length > 0 && (
                        <p className="text-[10px] text-gray-400 mt-1.5">
                          {selectedWindows.length} window{selectedWindows.length > 1 ? 's' : ''} selected
                        </p>
                      )}
                    </div>
                  </>
                ) : assignedStaffId && !assignedStaff ? (
                  <div className="flex items-center justify-between bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
                    <p className="text-xs text-yellow-700">Staff assigned (ID: {assignedStaffId.slice(-6)})</p>
                    <button
                      onClick={() => handleRemoveAssignment(index)}
                      className="text-xs text-red-500 hover:text-red-700 font-medium"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setActiveServiceIndex(isDropdownOpen ? null : index)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-gray-400 hover:text-gray-700 transition-colors"
                  >
                    <UserCheck className="w-4 h-4" />
                    Assign Staff
                  </button>
                )}

                {/* Staff Dropdown */}
                {isDropdownOpen && (
                  <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          placeholder="Search staff..."
                          value={staffSearch}
                          onChange={(e) => handleStaffSearch(e.target.value)}
                          className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {loadingStaff ? (
                        <p className="text-xs text-gray-400 text-center py-4">Loading staff...</p>
                      ) : staffList.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-4">No staff found</p>
                      ) : (
                        staffList.map((staff) => {
                          const isAlreadyAssigned = Object.values(assignments).some(
                            (a) => a?.staffId === staff._id
                          ) && assignments[index]?.staffId !== staff._id;
                          const availability = getStaffAvailability(staff);
                          const isUnavailable = !availability.available;
                          const staffWindows = getStaffWindowsForDay(staff, booking.bookingDate);

                          return (
                            <button
                              key={staff._id}
                              onClick={() => !isAlreadyAssigned && !isUnavailable && handleAssign(index, staff._id)}
                              disabled={isAlreadyAssigned || isUnavailable}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                                isAlreadyAssigned || isUnavailable
                                  ? 'opacity-50 cursor-not-allowed bg-gray-50'
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                                isUnavailable ? 'bg-red-100' : 'bg-gray-200'
                              }`}>
                                <span className={`text-xs font-medium ${isUnavailable ? 'text-red-600' : 'text-gray-600'}`}>
                                  {staff.name?.charAt(0)?.toUpperCase() || '?'}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-900 truncate">{staff.name}</p>
                                {isUnavailable ? (
                                  <div className="flex items-center gap-1 mt-0.5">
                                    {availability.type === 'busy' ? (
                                      <Clock className="w-3 h-3 text-orange-500 flex-shrink-0" />
                                    ) : (
                                      <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />
                                    )}
                                    <p className="text-[10px] text-red-500 truncate">{availability.reason}</p>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1 mt-0.5">
                                    <p className="text-[10px] text-gray-500">{staff.phoneNumber}</p>
                                    {staffWindows.length > 0 && (
                                      <span className="text-[10px] text-blue-500 ml-1">
                                        ({staffWindows.length} slot{staffWindows.length > 1 ? 's' : ''})
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              {isAlreadyAssigned && (
                                <span className="text-[10px] text-gray-400 flex-shrink-0">Assigned</span>
                              )}
                              {!isUnavailable && !isAlreadyAssigned && (
                                <span className="text-[10px] text-green-500 flex-shrink-0">Available</span>
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {(!booking.services || booking.services.length === 0) && (
            <p className="text-sm text-gray-400 text-center py-8">No services in this booking</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Assignments'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignStaffModal;
